import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Text,
  Box,
  Container,
  Button,
  Grid,
  GridItem,
  Link,
  Divider,
  useColorModeValue,
  HStack,
  IconButton,
  Spacer,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import SearchPager from "../SearchPager";

import { conferenceServiceItems } from "../DATABASE/serviceConference";
import { editorialItems } from "../DATABASE/serviceEditorial";
import { technicalItems } from "../DATABASE/serviceTechnical";

function matchesQuery(item, query, keys) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return keys.some((k) => String(item?.[k] ?? "").toLowerCase().includes(q));
}

function scrollToId(id) {
  const target = document.getElementById(id);
  const offset = 85; // fixed navbar offset
  if (target) {
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

function paginate(list, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    totalPages,
    safePage,
    pageItems: list.slice(start, start + pageSize),
  };
}

export default function ServiceAndMembership() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const textColor = useColorModeValue("gray.700", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sectionBg = useColorModeValue("white", "gray.800");

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
    cursor: "pointer",
  };

  // One global search for all sections
  const [query, setQuery] = useState("");

  // predefined number of items per page (per section)
  const PAGE_SIZE = 8;

  // per-section page state
  const [pEditorial, setPEditorial] = useState(1);
  const [pTechnical, setPTechnical] = useState(1);
  const [pConference, setPConference] = useState(1);
  const [pOther, setPOther] = useState(1);
  const [pMemberships, setPMemberships] = useState(1);

  // reset pages whenever query changes (avoid empty pages)
  useEffect(() => {
    setPEditorial(1);
    setPTechnical(1);
    setPConference(1);
    setPOther(1);
    setPMemberships(1);
  }, [query]);

  // Other + Memberships data
  const otherItems = useMemo(
    () => [
      {
        date: "2021–2022",
        title: "Founding Faculty Sponsor",
        text:
          "Association for Computing Machinery (ACM)-Women in Computing uOttawa Chapter. (This chapter was founded by 10 women graduate students, 7 of which were in my research team with the founding chair and vice-chair being my PhD students. This chapter was one of the only two ACM-W chapters in Canada when it was founded.)",
      },
    ],
    []
  );

  const memberships = useMemo(
    () => [
      { text: "IEEE Senior Member (S 05, M 09, SM 12)" },
      { text: "ACM Senior Member (M 15, SM 20)" },
      { text: "Member, Sigma Xi, The Scientific Research Honor Society (ΣΞ)" },
      { text: "IEEE Systems Council Member" },
      { text: "IEEE Sensors Council Member" },
      { text: "IEEE Vehicular Technology Society Member" },
      { text: "IEEE Intelligent Transportation Systems Society Member" },
      { text: "IEEE Communications Society Member" },
      { text: "IEEE Sustainable ICT Community, Member" },
      { text: "Professional Engineers Ontario (PEO)" },
    ],
    []
  );

  // Global filtered lists (single query)
  const editorialFiltered = useMemo(
    () => editorialItems.filter((x) => matchesQuery(x, query, ["date", "title", "text"])),
    [query]
  );

  const technicalFiltered = useMemo(
    () => technicalItems.filter((x) => matchesQuery(x, query, ["date", "title", "text"])),
    [query]
  );

  const conferenceFiltered = useMemo(
    () => conferenceServiceItems.filter((x) => matchesQuery(x, query, ["date", "title", "text"])),
    [query]
  );

  const otherFiltered = useMemo(
    () => otherItems.filter((x) => matchesQuery(x, query, ["date", "title", "text"])),
    [query, otherItems]
  );

  const membershipsFiltered = useMemo(
    () => memberships.filter((x) => matchesQuery(x, query, ["text"])),
    [query, memberships]
  );

  // Pagination per section
  const editorialPg = useMemo(
    () => paginate(editorialFiltered, pEditorial, PAGE_SIZE),
    [editorialFiltered, pEditorial]
  );
  const technicalPg = useMemo(
    () => paginate(technicalFiltered, pTechnical, PAGE_SIZE),
    [technicalFiltered, pTechnical]
  );
  const conferencePg = useMemo(
    () => paginate(conferenceFiltered, pConference, PAGE_SIZE),
    [conferenceFiltered, pConference]
  );
  const otherPg = useMemo(() => paginate(otherFiltered, pOther, PAGE_SIZE), [otherFiltered, pOther]);
  const membershipsPg = useMemo(
    () => paginate(membershipsFiltered, pMemberships, PAGE_SIZE),
    [membershipsFiltered, pMemberships]
  );

  const searchActive = query.trim().length > 0;

  // Sections config
  const sections = useMemo(() => {
    return [
      {
        id: "Technical",
        label: "Committee / Council / Board Services",
        total: technicalItems.length,
        filtered: technicalFiltered.length,
        pager: technicalPg,
        setPage: setPTechnical,
        render: (items) => (
          <Grid templateColumns={{ base: "1fr", md: "140px 220px 1fr" }} gap={4}>
            {items.map((item, idx) => (
              <React.Fragment key={`${item.title}-${idx}`}>
                <GridItem fontWeight="800" color={textColor}>
                  {item.date}
                </GridItem>
                <GridItem fontStyle="italic" color={textColor}>
                  {item.title}
                </GridItem>
                <GridItem color={textColor} lineHeight="1.7">
                  {item.text}
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        ),
      },
      {
        id: "Editorial",
        label: "Editorial Activities",
        total: editorialItems.length,
        filtered: editorialFiltered.length,
        pager: editorialPg,
        setPage: setPEditorial,
        render: (items) => (
          <Grid templateColumns={{ base: "1fr", md: "140px 220px 1fr" }} gap={4}>
            {items.map((item, idx) => (
              <React.Fragment key={`${item.title}-${idx}`}>
                <GridItem fontWeight="800" color={textColor}>
                  {item.date}
                </GridItem>
                <GridItem fontStyle="italic" color={textColor}>
                  {item.title}
                </GridItem>
                <GridItem color={textColor} lineHeight="1.7">
                  {item.text}
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        ),
      },
      {
        id: "Conference",
        label: "Conference / Workshop / Track Chair",
        total: conferenceServiceItems.length,
        filtered: conferenceFiltered.length,
        pager: conferencePg,
        setPage: setPConference,
        render: (items) => (
          <Grid templateColumns={{ base: "1fr", md: "140px 220px 1fr" }} gap={4}>
            {items.map((item, idx) => (
              <React.Fragment key={`${item.title}-${idx}`}>
                <GridItem fontWeight="800" color={textColor}>
                  {item.date}
                </GridItem>
                <GridItem fontStyle="italic" color={textColor}>
                  {item.title}
                </GridItem>
                <GridItem color={textColor} lineHeight="1.7">
                  {item.text}
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        ),
      },
      {
        id: "Other",
        label: "Other Professional Activities",
        total: otherItems.length,
        filtered: otherFiltered.length,
        pager: otherPg,
        setPage: setPOther,
        render: (items) => (
          <Grid templateColumns={{ base: "1fr", md: "140px 220px 1fr" }} gap={4}>
            {items.map((item, idx) => (
              <React.Fragment key={`${item.title}-${idx}`}>
                <GridItem fontWeight="800" color={textColor}>
                  {item.date}
                </GridItem>
                <GridItem fontStyle="italic" color={textColor}>
                  {item.title}
                </GridItem>
                <GridItem color={textColor} lineHeight="1.7">
                  {item.text}
                </GridItem>
              </React.Fragment>
            ))}
          </Grid>
        ),
      },
      {
        id: "Memberships",
        label: "Memberships",
        total: memberships.length,
        filtered: membershipsFiltered.length,
        pager: membershipsPg,
        setPage: setPMemberships,
        render: (items) => (
          <Box>
            {items.map((m, idx) => (
              <Text key={idx} color={textColor} lineHeight="1.9">
                • {m.text}
              </Text>
            ))}
          </Box>
        ),
      },
    ];
  }, [
    technicalFiltered.length,
    editorialFiltered.length,
    conferenceFiltered.length,
    otherFiltered.length,
    membershipsFiltered.length,
    technicalPg,
    editorialPg,
    conferencePg,
    otherPg,
    membershipsPg,
    otherItems.length,
    memberships.length,
    textColor,
  ]);

  const visibleSections = useMemo(() => {
    if (!searchActive) return sections;
    return sections.filter((s) => s.filtered > 0);
  }, [sections, searchActive]);

  return (
    <>
      <Container maxW="1100px" py={5}>
        <Text fontSize={{ base: "26px", md: "32px" }} fontWeight="800" textAlign="center">
          Service and Memberships
        </Text>

        <Text textAlign="center" mt={2} fontSize="lg" color={textColor}>
          <Link href="/" {...linkStyle}>
            Dr. Burak Kantarci
          </Link>
        </Text>

        <Divider mt={6} />
      </Container>

      <Container maxW="1200px" pb={14}>
        {/* Navigation Buttons */}
        {!searchActive && (
          <Flex justify="center" mt={2} gap={2} wrap="wrap">
            {sections.map((s) => (
              <Button
                key={s.id}
                onClick={() => scrollToId(s.id)}
                variant="outline"
                borderRadius="999px"
                bg={useColorModeValue("white", "gray.900")}
                borderColor={borderColor}
                _hover={{
                  borderColor: uoRed,
                  color: uoRed,
                  bg: useColorModeValue("rgba(143,0,26,0.06)", "rgba(143,0,26,0.18)"),
                }}
                fontWeight="700"
                px={5}
              >
                {s.label}
              </Button>
            ))}
          </Flex>
        )}

        {/* ONE Global Search (Search only at top) */}
        <Box mt={8}>
          <SearchPager
            mode="search"
            title="Search in Service & Memberships"
            query={query}
            onQueryChange={setQuery}
            page={1}
            pageSize={PAGE_SIZE}
            totalItems={
              editorialItems.length +
              technicalItems.length +
              conferenceServiceItems.length +
              otherItems.length +
              memberships.length
            }
            filteredItems={
              editorialFiltered.length +
              technicalFiltered.length +
              conferenceFiltered.length +
              otherFiltered.length +
              membershipsFiltered.length
            }
            onPrev={() => {}}
            onNext={() => {}}
          />
        </Box>

        {/* When searching and nothing matches */}
        {searchActive && visibleSections.length === 0 && (
          <Box mt={8} p={6} border={`1px solid ${borderColor}`} borderRadius="18px">
            <Text color="gray.500">No results found for “{query}”.</Text>
          </Box>
        )}

        {/* Sections */}
        {visibleSections.map((s) => (
          <Box
            key={s.id}
            id={s.id}
            mt={10}
            bg={sectionBg}
            border={`1px solid ${borderColor}`}
            borderRadius="18px"
            p={{ base: 4, md: 6 }}
          >
            {/* ====== UPDATED HEADER: title top row, pager below ====== */}
            <Box mb={3}>
              {/* Row 1: Title (left) + Back to top (right) */}
              <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                <Text fontSize="xl" fontWeight="800">
                  {s.label}
                </Text>

                <Link
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  fontSize="sm"
                  fontWeight="600"
                  color={uoRed}
                  textDecoration="underline"
                  _hover={{ color: uoRedHover }}
                >
                  Back to top
                </Link>
              </Flex>

              {/* Row 2: Pager UNDER title (left aligned) */}
              {s.pager.totalPages > 1 && (
                <Flex mt={2} align="center" justify="flex-start">
                  <HStack spacing={2} flexWrap="wrap">
                    <IconButton
                      aria-label="Previous page"
                      icon={<FiChevronLeft />}
                      onClick={() => s.setPage((p) => Math.max(1, p - 1))}
                      isDisabled={s.pager.safePage <= 1}
                      variant="outline"
                      size="sm"
                      borderRadius="999px"
                    />
                    <Text fontSize="sm" minW="85px" textAlign="center">
                      Page{" "}
                      <Text as="span" fontWeight="700">
                        {s.pager.safePage}
                      </Text>{" "}
                      / {s.pager.totalPages}
                    </Text>
                    <IconButton
                      aria-label="Next page"
                      icon={<FiChevronRight />}
                      onClick={() => s.setPage((p) => Math.min(s.pager.totalPages, p + 1))}
                      isDisabled={s.pager.safePage >= s.pager.totalPages}
                      variant="outline"
                      size="sm"
                      borderRadius="999px"
                    />
                  </HStack>
                </Flex>
              )}
            </Box>
            {/* ====== END UPDATED HEADER ====== */}

            {s.pager.pageItems.length > 0 ? (
              s.render(s.pager.pageItems)
            ) : (
              <Text color="gray.500">No items match your search.</Text>
            )}
          </Box>
        ))}
      </Container>
    </>
  );
}